// Оркестрация загрузки партий поверх @ext/services/chess-apis.
// Реэкспортируемые из расширения функции (реальные сигнатуры):
//   fetchLichessCoachData(nick, {signal?}) → { nick, ratings, games: CoachGame[] }
//   fetchChessComCoachData(nick, {signal?}) → { nick, ratings, games: CoachGame[] }
// Оба валидируют ник (isValidNick) и бросают Error('nick_not_found') на 404.
import { fetchLichessCoachData, fetchChessComCoachData } from '@ext/services/chess-apis';
import { type CoachGame, MAX_GAMES } from '@ext/core/game-sources';

export type GameSource = 'lichess' | 'chesscom' | 'auto';

/** Слияние партий из нескольких источников: dedup по id, сортировка по endTime
 *  (свежие сверху), обрезка до MAX_GAMES. Чистая — покрыта юнит-тестом. */
export function mergeAndSort(...lists: CoachGame[][]): CoachGame[] {
  const seen = new Set<string>();
  const merged: CoachGame[] = [];
  for (const list of lists) {
    for (const game of list) {
      const key = `${game.site}:${game.id}`;
      if (game.id && seen.has(key)) continue;
      if (game.id) seen.add(key);
      merged.push(game);
    }
  }
  merged.sort((a, b) => (b.endTime || 0) - (a.endTime || 0));
  return merged.slice(0, MAX_GAMES);
}

/** Есть ли в PGN встроенные оценки Lichess [%eval] → бейдж «мгновенно». */
export function hasEvals(pgn: string): boolean {
  return /\[%eval/.test(pgn || '');
}

/** Из набора reject-причин выбрать самую информативную (сеть важнее «не найден»). */
function pickError(reasons: unknown[]): Error {
  const msgs = reasons.map((r) => (r instanceof Error ? r : new Error(String(r))));
  const network = msgs.find((e) => e.message !== 'nick_not_found');
  return network || msgs[0] || new Error('nick_not_found');
}

/** Загрузка партий по нику из источника. auto = оба параллельно + merge. */
export async function fetchGames(
  source: GameSource,
  nick: string,
  signal?: AbortSignal,
): Promise<CoachGame[]> {
  if (source === 'lichess') {
    return mergeAndSort((await fetchLichessCoachData(nick, { signal })).games);
  }
  if (source === 'chesscom') {
    return mergeAndSort((await fetchChessComCoachData(nick, { signal })).games);
  }
  const settled = await Promise.allSettled([
    fetchLichessCoachData(nick, { signal }),
    fetchChessComCoachData(nick, { signal }),
  ]);
  const lists: CoachGame[][] = [];
  const errors: unknown[] = [];
  for (const r of settled) {
    if (r.status === 'fulfilled') lists.push(r.value.games);
    else errors.push(r.reason);
  }
  if (!lists.length) throw pickError(errors);
  return mergeAndSort(...lists);
}
